import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Lock, Flame, Send, Search, Eye } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SubmitProjectDialog from "@/components/SubmitProjectDialog";
import WinMarquee from "@/components/WinMarquee";

export default function ProjectsSection() {
  const { user, openAuth } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [visible, setVisible] = useState(9);
  const [tagFilter, setTagFilter] = useState("全部");
  const [tagsExpanded, setTagsExpanded] = useState(false);
  const [query, setQuery] = useState("");
  const [submitOpen, setSubmitOpen] = useState(false);

  const allTags = useMemo(() => {
    const m = {};
    projects.forEach((p) => (p.tags || []).forEach((t) => { m[t] = (m[t] || 0) + 1; }));
    return Object.entries(m).sort((a, b) => b[1] - a[1]).map(([t]) => t);
  }, [projects]);

  const shownTags = tagsExpanded ? allTags : allTags.slice(0, 12);

  const filtered = projects.filter((p) => {
    if (tagFilter !== "全部" && !(p.tags || []).includes(tagFilter)) return false;
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      return (p.title + p.summary + (p.tags || []).join("")).toLowerCase().includes(q);
    }
    return true;
  });

  const hotProjects = useMemo(
    () => [...projects].sort((a, b) => ((b.is_pinned ? 1 : 0) - (a.is_pinned ? 1 : 0)) || (b.views || 0) - (a.views || 0)).slice(0, 3),
    [projects]
  );

  const viewedRef = useRef(new Set());
  const trackView = (pid) => {
    if (viewedRef.current.has(pid)) return;
    viewedRef.current.add(pid);
    api.post(`/projects/${pid}/view`).then(() => {
      setProjects((list) => list.map((p) => (p.pid === pid ? { ...p, views: (p.views || 0) + 1 } : p)));
    }).catch(() => {});
  };

  useEffect(() => {
    api.get("/projects/public").then((r) => setProjects(r.data)).catch(() => {});
  }, [user]);

  const unlock = () => {
    if (user) navigate("/me");
    else openAuth();
  };

  const openSubmit = () => {
    if (user) setSubmitOpen(true);
    else openAuth();
  };

  return (
    <section id="projects" className="relative py-24 lg:py-36 px-5 lg:px-8" data-testid="projects-section">
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-wrap items-end justify-between gap-6 mb-6"
        >
          <div>
            <p className="font-mono-num text-xs tracking-[0.35em] text-[#D4AF37] mb-4">TODAY'S PROJECTS · 今日项目</p>
            <h2 className="font-serif-sc text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              今日优选<span className="text-gold-grad">项目</span>
            </h2>
          </div>
          <div className="flex flex-col items-start md:items-end gap-4">
            <p className="max-w-xs text-sm text-gray-500 leading-relaxed md:text-right">
              每日更新 · 激活会员进入项目交流群，查看完整拆解
            </p>
            <button
              data-testid="submit-project-btn"
              onClick={openSubmit}
              className="btn-ghost-gold px-5 py-2.5 text-xs inline-flex items-center gap-2"
            >
              <Send className="w-3.5 h-3.5" /> 项目投稿 · 奖励最高 ¥188,888
            </button>
          </div>
        </motion.div>
        <p className="text-xs text-gray-600 mb-8">
          手上有靠谱项目？投稿给平台，专业团队审核评估，一经录用奖励最高 <span className="text-[#D4AF37] font-mono-num">¥188,888</span>
        </p>

        <div className="mb-14">
          <WinMarquee />
        </div>

        {hotProjects.length > 0 && (
          <div className="mb-14" data-testid="hot-projects-window">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-9 h-9 grid place-items-center border border-[#D4AF37]/40 bg-[#D4AF37]/10">
                <Flame className="w-4.5 h-4.5 text-[#D4AF37]" />
              </span>
              <div>
                <p className="font-serif-sc text-lg font-bold">热门项目 <span className="font-mono-num text-xs tracking-[0.3em] text-[#D4AF37] ml-1">HOT PICKS</span></p>
                <p className="text-[11px] text-gray-600 tracking-widest">会员围观人数实时更新 · 置顶项目优先展示</p>
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              {hotProjects.map((p, i) => (
                <motion.article
                  key={p.pid}
                  data-testid={`hot-project-card-${i}`}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: i * 0.1 }}
                  onClick={() => trackView(p.pid)}
                  className="relative border border-[#D4AF37]/30 bg-gradient-to-b from-[#14141B] to-[#0A0A0E] p-6 flex flex-col cursor-pointer hover:border-[#D4AF37]/60 transition-colors"
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-mono-num text-3xl font-black text-gold-grad">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-[10px] tracking-widest px-2 py-0.5 bg-gradient-to-r from-[#F3E5AB] via-[#D4AF37] to-[#AA7C11] text-[#070709] font-bold inline-flex items-center gap-1">
                      <Flame className="w-3 h-3" /> HOT
                    </span>
                  </div>
                  <h3 className="font-serif-sc text-lg font-bold mb-2">{p.title}</h3>
                  <div className="flex items-center gap-3 mb-3 text-[11px] font-mono-num">
                    <span className="text-[#D4AF37]">日赚{p.daily_income}</span>
                    <span className="text-gray-500 inline-flex items-center gap-1" data-testid={`hot-project-views-${i}`}>
                      <Eye className="w-3.5 h-3.5" /> {(p.views || 0).toLocaleString()} 人围观
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {(p.tags || []).slice(0, 3).map((t) => (
                      <span key={t} className="text-[10px] tracking-widest text-gray-400 border border-[#D4AF37]/15 px-2 py-0.5">{t}</span>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 leading-relaxed mb-4">{p.summary}</p>
                  {p.locked ? (
                    <div className="relative mt-auto">
                      <div className="blur-sm select-none text-[11px] text-gray-500 leading-relaxed border border-[#D4AF37]/10 p-3 h-16 overflow-hidden" aria-hidden>
                        项目完整拆解：渠道对接、操作SOP、放大路径、风险规避、实测收益数据与复盘……激活后全部可见
                      </div>
                      <div className="absolute inset-0 grid place-items-center bg-[#070709]/60">
                        <button
                          data-testid={`hot-project-unlock-btn-${i}`}
                          onClick={(e) => { e.stopPropagation(); unlock(); }}
                          className="btn-gold px-4 py-2 text-[11px] inline-flex items-center gap-1.5"
                        >
                          <Lock className="w-3 h-3" /> 激活卡密查看完整拆解
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-auto text-[11px] text-gray-300 leading-relaxed border border-[#D4AF37]/20 bg-[#D4AF37]/5 p-3 whitespace-pre-wrap" data-testid={`hot-project-detail-${i}`}>
                      {p.detail}
                    </div>
                  )}
                </motion.article>
              ))}
            </div>
          </div>
        )}

        {projects.length > 0 && (
          <div className="mb-8">
            <div className="relative mb-4" data-testid="project-search-wrap">
              <Search className="w-4 h-4 text-gray-500 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                data-testid="project-search-input"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setVisible(9); }}
                placeholder="搜索项目关键词，如：闲鱼 / AI / 同城 / 带货"
                className="input-gold w-full pl-11 pr-4 py-3 text-sm"
              />
            </div>
            <div
              className="flex gap-2 overflow-x-auto pb-3"
              data-lenis-prevent
              data-testid="project-tag-filter"
              onWheel={(e) => {
                if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) e.currentTarget.scrollLeft += e.deltaY;
              }}
            >
              {["全部", ...shownTags].map((t) => (
                <button
                  key={t}
                  data-testid={`tag-filter-${t}`}
                  onClick={() => { setTagFilter(t); setVisible(9); }}
                  className={`px-4 py-1.5 text-xs tracking-widest whitespace-nowrap border transition-colors ${
                    tagFilter === t ? "border-[#D4AF37]/60 text-[#D4AF37] bg-[#D4AF37]/10" : "border-[#D4AF37]/15 text-gray-500 hover:text-gray-300"
                  }`}
                >
                  {t}
                </button>
              ))}
              {allTags.length > 12 && (
                <button
                  data-testid="tag-expand-btn"
                  onClick={() => setTagsExpanded((v) => !v)}
                  className="px-4 py-1.5 text-xs tracking-widest whitespace-nowrap border border-dashed border-[#D4AF37]/40 text-[#D4AF37]/80 hover:text-[#D4AF37] transition-colors"
                >
                  {tagsExpanded ? "收起" : `更多 +${allTags.length - 12}`}
                </button>
              )}
            </div>
          </div>
        )}
        {filtered.length === 0 && projects.length > 0 && (
          <p className="text-sm text-gray-500 py-12 text-center" data-testid="search-empty">没有匹配「{query || tagFilter}」的项目，换个关键词试试</p>
        )}
        {filtered.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.slice(0, visible).map((p, i) => (
              <motion.article
                key={p.pid}
                data-testid={`project-card-${i}`}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: (i % 9) * 0.08 }}
                className="panel p-7 flex flex-col cursor-pointer"
                onClick={() => trackView(p.pid)}
              >
                <div className="flex items-center justify-between mb-5">
                  <span className="font-mono-num text-xs text-[#D4AF37] border border-[#D4AF37]/40 px-2.5 py-1 inline-flex items-center gap-1">
                    <Flame className="w-3.5 h-3.5" /> 日赚{p.daily_income}
                  </span>
                  <span className="font-mono-num text-[10px] text-gray-600 inline-flex items-center gap-2">
                    <span className="inline-flex items-center gap-1" data-testid={`project-views-${i}`}><Eye className="w-3 h-3" /> {(p.views || 0).toLocaleString()}</span>
                    收录 {(p.created_at || "").slice(0, 10)}
                  </span>
                </div>
                <h3 className="font-serif-sc text-xl font-bold mb-3">{p.title}</h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  {p.tags.map((t) => (
                    <span key={t} className="text-[10px] tracking-widest text-gray-400 border border-[#D4AF37]/15 px-2 py-0.5">
                      {t}
                    </span>
                  ))}
                </div>
                <p className="text-sm text-gray-400 leading-relaxed mb-5">{p.summary}</p>
                {p.locked ? (
                  <div className="relative mt-auto">
                    <div
                      className="blur-sm select-none text-xs text-gray-500 leading-relaxed border border-[#D4AF37]/10 p-4 h-24 overflow-hidden"
                      aria-hidden
                    >
                      项目完整拆解：渠道对接、操作SOP、放大路径、风险规避、实测收益数据与复盘……激活后全部可见
                    </div>
                    <div className="absolute inset-0 grid place-items-center bg-[#070709]/60">
                      <button
                        data-testid={`project-unlock-btn-${i}`}
                        onClick={unlock}
                        className="btn-gold px-5 py-2.5 text-xs inline-flex items-center gap-2"
                      >
                        <Lock className="w-3.5 h-3.5" /> 激活卡密查看完整拆解
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    className="mt-auto text-xs text-gray-300 leading-relaxed border border-[#D4AF37]/20 bg-[#D4AF37]/5 p-4 whitespace-pre-wrap"
                    data-testid={`project-detail-${i}`}
                  >
                    {p.detail}
                  </div>
                )}
              </motion.article>
            ))}
          </div>
        )}
        {visible < filtered.length && (
          <div className="text-center mt-12" data-testid="load-more-wrap">
            <p className="text-xs text-gray-600 font-mono-num mb-4">已展示 {Math.min(visible, filtered.length)} / {filtered.length} 个项目</p>
            <button
              data-testid="load-more-projects"
              onClick={() => setVisible((v) => v + 9)}
              className="btn-ghost-gold px-10 py-3 text-sm"
            >
              加载更多优选项目
            </button>
          </div>
        )}
        <SubmitProjectDialog open={submitOpen} onClose={() => setSubmitOpen(false)} />
      </div>
    </section>
  );
}
